import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, IconButton, ToggleButton, ToggleButtonGroup,
  TextField, Divider, List, ListItem, ListItemText, ListItemSecondaryAction, Paper,
  Tooltip, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  CircularProgress, Slider, Chip,
} from '@mui/material';
import { ArrowBack, Save, Delete, Timeline, CropSquare, LocationOn, ZoomIn, ZoomOut, PanTool, Refresh, CenterFocusStrong, MyLocation, GpsFixed, FitScreen } from '@mui/icons-material';
import { Stage, Layer, Line, Circle, Rect, Text, Image as KonvaImage } from 'react-konva';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppHooks';
import { updateMapAnnotations } from '../../features/maps/mapsSlice';
import { fetchMyRobots } from '../../features/robots/robotsSlice';
import { Wall, Zone, Waypoint } from '../../types';
import { useWebSocket } from '../../hooks/useWebSocket';
import apiClient from '../../utils/apiClient';

type ToolType = 'select' | 'wall' | 'zone' | 'waypoint';

const MapEditor: React.FC = () => {
  const { mapId } = useParams<{ mapId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const stageRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  
  // Get robots for live pose
  const { robots } = useAppSelector((state) => state.robots);
  const { getRobotTelemetry, robotsOnline, subscribeToRobot } = useWebSocket();
  
  // Get robot ID from URL params if available
  const robotIdFromUrl = searchParams.get('robotId');
  const selectedRobot = robots.find(r => r.id === robotIdFromUrl) || robots[0];

  const [mapData, setMapData] = useState<any>(null);
  const [mapImage, setMapImage] = useState<HTMLImageElement | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [tool, setTool] = useState<ToolType>('select');
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState<number[]>([]);
  const [walls, setWalls] = useState<Wall[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [waypointDialog, setWaypointDialog] = useState({ open: false, x: 0, y: 0, yaw: 0 });
  const [waypointName, setWaypointName] = useState('');
  const [containerSize, setContainerSize] = useState({ width: 900, height: 600 });
  const [imageSize, setImageSize] = useState({ width: 900, height: 600 });
  const [robotPoseDialog, setRobotPoseDialog] = useState({ open: false });
  
  // Fetch robots on mount
  useEffect(() => {
    if (robots.length === 0) dispatch(fetchMyRobots());
  }, [dispatch, robots.length]);
  
  // Subscribe to robot for live pose
  useEffect(() => {
    if (selectedRobot?.serial_number) {
      subscribeToRobot(selectedRobot.serial_number);
    }
  }, [selectedRobot, subscribeToRobot]);
  
  // Get current robot telemetry
  const telemetry = selectedRobot ? getRobotTelemetry(selectedRobot.serial_number) : null;
  const isRobotOnline = selectedRobot ? robotsOnline.includes(selectedRobot.serial_number) : false;
  const robotPose = telemetry?.pose;

  // Coordinate conversion — UN-FLIPPED PGM image (served as-is by backend).
  // PGM file: row 0 = top of file. Browser displays row 0 at screen top.
  // ROS2 maps: origin is bottom-left, Y increases upward.
  // PGM pixel (px, py):
  //   world_x = origin_x + px * resolution
  //   world_y = origin_y + (height - 1 - py) * resolution
  // (py=0 is top row = highest world_y, py=height-1 is bottom = origin_y)
  
  const getOrigin = (): { x: number; y: number } => {
    if (!mapData?.origin) return { x: 0, y: 0 };
    if (Array.isArray(mapData.origin)) {
      return { x: mapData.origin[0] || 0, y: mapData.origin[1] || 0 };
    }
    return { x: mapData.origin.x || 0, y: mapData.origin.y || 0 };
  };
  
  const getResolution = (): number => {
    return mapData?.resolution || 0.05;
  };
  
  const pixelToWorld = (px: number, py: number): { x: number; y: number } => {
    const resolution = getResolution();
    const origin = getOrigin();
    const imgHeight = imageSize.height;
    return {
      x: origin.x + px * resolution,
      y: origin.y + (imgHeight - 1 - py) * resolution
    };
  };
  
  const worldToPixel = (wx: number, wy: number): { x: number; y: number } => {
    const resolution = getResolution();
    const origin = getOrigin();
    const imgHeight = imageSize.height;
    return {
      x: (wx - origin.x) / resolution,
      y: imgHeight - 1 - (wy - origin.y) / resolution
    };
  };

  // Resize observer to make canvas responsive
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({
          width: Math.max(rect.width - 20, 400),
          height: Math.max(window.innerHeight - 280, 400)
        });
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const fetchMap = async () => {
      try {
        const response = await apiClient.get(`/maps/${mapId}`);
        setMapData(response.data);
        if (response.data.annotations) {
          // Support both key formats: 'walls'/'zones' (new) and 'virtual_walls'/'keepout_zones' (old)
          const walls = response.data.annotations.walls || response.data.annotations.virtual_walls || [];
          const zones = response.data.annotations.zones || response.data.annotations.keepout_zones || [];
          setWalls(walls);
          setZones(zones);
          setWaypoints(response.data.annotations.waypoints || []);
          console.log(`[MapEditor] Loaded annotations: ${walls.length} walls, ${zones.length} zones, ${response.data.annotations.waypoints?.length || 0} waypoints`);
        }
        
        // Load map image if available
        if (response.data.map_file_url) {
          loadMapImage(response.data.map_file_url);
        } else {
          createPlaceholderImage();
        }
      } catch (error) {
        console.error('Failed to load map:', error);
        setSnackbar({ open: true, message: 'Failed to load map' });
        createPlaceholderImage();
      }
    };
    if (mapId) fetchMap();
  }, [mapId]);

  const loadMapImage = async (filePath: string) => {
    setIsLoadingImage(true);
    try {
      // Fetch the map file from server (already flipped for correct ROS2 display)
      const response = await apiClient.get(`/maps/${mapId}/image`, {
        responseType: 'blob',
      });
      
      // Create a URL for the blob
      const imageUrl = URL.createObjectURL(response.data);
      
      // Load as image - server already handles vertical flip for ROS2 coordinate display
      const img = new window.Image();
      img.onload = () => {
        setMapImage(img);
        setImageSize({ width: img.width, height: img.height });
        imageRef.current = img;
        
        // Auto-fit zoom using actual container dimensions
        const container = containerRef.current;
        const cw = container ? Math.max(container.getBoundingClientRect().width - 20, 400) : containerSize.width;
        const ch = container ? Math.max(window.innerHeight - 280, 400) : containerSize.height;
        
        const fitZoomX = (cw * 0.9) / img.width;
        const fitZoomY = (ch * 0.9) / img.height;
        const fitZoom = Math.min(fitZoomX, fitZoomY);
        const clampedZoom = Math.max(0.1, Math.min(5, fitZoom));
        setZoom(clampedZoom);
        
        // Center the image
        setStagePosition({
          x: (cw - img.width * clampedZoom) / 2,
          y: (ch - img.height * clampedZoom) / 2
        });
        
        setIsLoadingImage(false);
        URL.revokeObjectURL(imageUrl);
      };
      img.onerror = () => {
        console.error('Failed to load map image');
        setIsLoadingImage(false);
        createPlaceholderImage();
      };
      img.src = imageUrl;
    } catch (error) {
      console.error('Error fetching map image:', error);
      setIsLoadingImage(false);
      createPlaceholderImage();
    }
  };

  const createPlaceholderImage = () => {
    const width = 800;
    const height = 600;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Dark background
      ctx.fillStyle = '#1e2530';
      ctx.fillRect(0, 0, width, height);
      
      // Grid
      ctx.strokeStyle = '#2a3441';
      ctx.lineWidth = 1;
      const gridSize = 50;
      for (let i = 0; i <= width; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, height);
        ctx.stroke();
      }
      for (let i = 0; i <= height; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(width, i);
        ctx.stroke();
      }
      
      // Center marker
      ctx.strokeStyle = '#3a4a5a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(width/2 - 30, height/2);
      ctx.lineTo(width/2 + 30, height/2);
      ctx.moveTo(width/2, height/2 - 30);
      ctx.lineTo(width/2, height/2 + 30);
      ctx.stroke();
      
      // Text
      ctx.fillStyle = '#5a6a7a';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No map image - draw annotations on grid', width / 2, height / 2 + 60);
    }
    
    const img = new window.Image();
    img.onload = () => {
      setMapImage(img);
      setImageSize({ width, height });
    };
    img.src = canvas.toDataURL();
  };

  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Get pointer position relative to the image (accounting for zoom and pan)
  const getRelativePointerPosition = (e: any) => {
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    
    // Convert screen coordinates to image coordinates
    const x = (pointerPos.x - stagePosition.x) / zoom;
    const y = (pointerPos.y - stagePosition.y) / zoom;
    
    return { x, y };
  };

  const handleMouseDown = (e: any) => {
    if (tool === 'select') return;
    
    const pos = getRelativePointerPosition(e);

    if (tool === 'waypoint') {
      setWaypointDialog({ open: true, x: pos.x, y: pos.y, yaw: 0 });
      return;
    }

    setIsDrawing(true);
    setCurrentPoints([pos.x, pos.y]);
  };

  const handleMouseMove = (e: any) => {
    if (!isDrawing || tool === 'select' || tool === 'waypoint') return;
    
    const pos = getRelativePointerPosition(e);

    if (tool === 'wall') {
      setCurrentPoints([currentPoints[0], currentPoints[1], pos.x, pos.y]);
    } else if (tool === 'zone') {
      const startX = currentPoints[0];
      const startY = currentPoints[1];
      setCurrentPoints([startX, startY, pos.x - startX, pos.y - startY]);
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (tool === 'wall' && currentPoints.length >= 4) {
      // Store both world and pixel coordinates for cross-platform compatibility
      const w1 = pixelToWorld(currentPoints[0], currentPoints[1]);
      const w2 = pixelToWorld(currentPoints[2], currentPoints[3]);
      const newWall: any = {
        id: generateId(),
        // Primary: world coordinates (compatible with robot dashboard / Nav2)
        points: [
          parseFloat(w1.x.toFixed(4)), parseFloat(w1.y.toFixed(4)),
          parseFloat(w2.x.toFixed(4)), parseFloat(w2.y.toFixed(4))
        ],
        // Cache: pixel coordinates for fast rendering
        pixel_points: [...currentPoints],
        name: `wall_${walls.length}`,
      };
      setWalls([...walls, newWall]);
      setIsDirty(true);
    } else if (tool === 'zone' && currentPoints.length >= 4 && Math.abs(currentPoints[2]) > 10 && Math.abs(currentPoints[3]) > 10) {
      const newZone: Zone = { id: generateId(), points: [...currentPoints], type: 'no_go', name: `Zone ${zones.length + 1}` };
      setZones([...zones, newZone]);
      setIsDirty(true);
    }
    setCurrentPoints([]);
  };

  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    const scaleBy = 1.15;
    const stage = stageRef.current;
    const oldScale = zoom;
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stagePosition.x) / oldScale,
      y: (pointer.y - stagePosition.y) / oldScale,
    };

    const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const clampedScale = Math.max(0.1, Math.min(10, newScale));  // Max 1000%

    setZoom(clampedScale);
    setStagePosition({
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    });
  };

  const handleDragEnd = (e: any) => {
    setStagePosition({
      x: e.target.x(),
      y: e.target.y(),
    });
  };

  const handleAddWaypoint = () => {
    if (!waypointName.trim()) return;
    
    // Convert pixel coordinates to world coordinates for navigation
    const worldCoords = pixelToWorld(waypointDialog.x, waypointDialog.y);
    
    // Validate coordinates are valid numbers
    if (isNaN(worldCoords.x) || isNaN(worldCoords.y)) {
      setSnackbar({ open: true, message: 'Error: Invalid coordinates. Make sure map data is loaded.' });
      return;
    }
    
    const newWaypoint: Waypoint = {
      id: generateId(),
      x: parseFloat(worldCoords.x.toFixed(4)),  // World X coordinate (rounded)
      y: parseFloat(worldCoords.y.toFixed(4)),  // World Y coordinate (rounded)
      theta: parseFloat((waypointDialog.yaw * Math.PI / 180).toFixed(4)),  // Convert degrees to radians
      name: waypointName,
      type: 'navigation',
      // Store pixel coords for display purposes
      pixel_x: Math.round(waypointDialog.x),
      pixel_y: Math.round(waypointDialog.y),
    };
    setWaypoints([...waypoints, newWaypoint]);
    setIsDirty(true);
    setWaypointDialog({ open: false, x: 0, y: 0, yaw: 0 });
    setWaypointName('');
  };

  const handleDeleteWall = (id: string) => { setWalls(walls.filter(w => w.id !== id)); setIsDirty(true); };
  const handleDeleteZone = (id: string) => { setZones(zones.filter(z => z.id !== id)); setIsDirty(true); };
  const handleDeleteWaypoint = (id: string) => { setWaypoints(waypoints.filter(w => w.id !== id)); setIsDirty(true); };
  
  // Add waypoint from robot's current pose
  const handleAddWaypointFromRobotPose = () => {
    if (!robotPose) {
      setSnackbar({ open: true, message: 'Robot pose not available. Make sure robot is online and localized.' });
      return;
    }
    
    if (!waypointName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a waypoint name.' });
      return;
    }
    
    // Robot pose is already in world coordinates
    const pixelCoords = worldToPixel(robotPose.x, robotPose.y);
    
    const newWaypoint: Waypoint = {
      id: generateId(),
      x: parseFloat(robotPose.x.toFixed(4)),  // World X
      y: parseFloat(robotPose.y.toFixed(4)),  // World Y
      theta: parseFloat((robotPose.theta || 0).toFixed(4)),  // Already in radians
      name: waypointName,
      type: 'navigation',
      pixel_x: Math.round(pixelCoords.x),
      pixel_y: Math.round(pixelCoords.y),
    };
    
    setWaypoints([...waypoints, newWaypoint]);
    setIsDirty(true);
    setRobotPoseDialog({ open: false });
    setWaypointName('');
    setSnackbar({ open: true, message: `Waypoint "${newWaypoint.name}" added at robot position!` });
  };

  const handleSave = async () => {
    try {
      await dispatch(updateMapAnnotations({
        mapId: mapId!,
        annotations: { walls, zones, waypoints },
      })).unwrap();
      setIsDirty(false);
      setSnackbar({ open: true, message: 'Map saved successfully!' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save map' });
    }
  };

  const handleResetView = () => {
    // Fit map to container with 90% margin
    const fitZoomX = (containerSize.width * 0.9) / imageSize.width;
    const fitZoomY = (containerSize.height * 0.9) / imageSize.height;
    const fitZoom = Math.min(fitZoomX, fitZoomY);
    
    // Clamp zoom between 0.1 and 5 (500%)
    const clampedZoom = Math.max(0.1, Math.min(5, fitZoom));
    setZoom(clampedZoom);
    
    // Center the image
    setStagePosition({
      x: (containerSize.width - imageSize.width * clampedZoom) / 2,
      y: (containerSize.height - imageSize.height * clampedZoom) / 2
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => {
          // Navigate back to maps list, preserving robot selection
          const robotId = searchParams.get('robotId') || mapData?.robot_id;
          navigate(robotId ? `/maps?robot=${robotId}` : '/maps');
        }}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>{mapData?.name || 'Map Editor'}</Typography>
          <Typography variant="body2" color="text.secondary">Draw walls, zones, and waypoints on the map</Typography>
        </Box>
        {isDirty && <Alert severity="warning" sx={{ py: 0 }}>Unsaved changes</Alert>}
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={!isDirty}>Save</Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 180px)' }}>
        {/* Canvas */}
        <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }} ref={containerRef}>
          <CardContent sx={{ p: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <ToggleButtonGroup value={tool} exclusive onChange={(_, v) => v && setTool(v)} size="small">
                <ToggleButton value="select"><Tooltip title="Pan/Select (drag to move)"><PanTool /></Tooltip></ToggleButton>
                <ToggleButton value="wall"><Tooltip title="Draw Virtual Wall"><Timeline /></Tooltip></ToggleButton>
                <ToggleButton value="zone"><Tooltip title="Draw No-Go Zone"><CropSquare /></Tooltip></ToggleButton>
                <ToggleButton value="waypoint"><Tooltip title="Add Waypoint (click on map)"><LocationOn /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
              
              <Divider orientation="vertical" flexItem />
              
              {/* Add waypoint from robot pose button */}
              <Tooltip title="Add waypoint at robot's current position">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GpsFixed />}
                  onClick={() => setRobotPoseDialog({ open: true })}
                  disabled={!isRobotOnline || !robotPose}
                  color="success"
                >
                  Robot Pose
                </Button>
              </Tooltip>
              {robotPose && isRobotOnline && (
                <Chip 
                  size="small" 
                  label={`(${robotPose.x.toFixed(2)}, ${robotPose.y.toFixed(2)})`}
                  color="success"
                  variant="outlined"
                />
              )}
              
              <Divider orientation="vertical" flexItem />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" onClick={() => setZoom(Math.max(zoom - 0.5, 0.1))}><ZoomOut /></IconButton>
                <Slider
                  value={zoom}
                  onChange={(_, v) => setZoom(v as number)}
                  min={0.1}
                  max={10}
                  step={0.1}
                  sx={{ width: 120 }}
                  size="small"
                />
                <IconButton size="small" onClick={() => setZoom(Math.min(zoom + 0.5, 10))}><ZoomIn /></IconButton>
                <Typography variant="body2" sx={{ minWidth: 55 }}>{Math.round(zoom * 100)}%</Typography>
              </Box>
              
              <Tooltip title="Fit to View">
                <IconButton size="small" onClick={handleResetView}><FitScreen /></IconButton>
              </Tooltip>
              
              <Box sx={{ flex: 1 }} />
              
              {mapData?.resolution && (
                <Typography variant="caption" color="text.secondary">
                  Resolution: {mapData.resolution.toFixed(3)} m/px | Size: {imageSize.width}×{imageSize.height}
                </Typography>
              )}
            </Box>

            {/* Canvas Stage */}
            <Paper 
              sx={{ 
                flex: 1, 
                bgcolor: '#0d1117', 
                overflow: 'hidden', 
                cursor: tool === 'select' ? 'grab' : 'crosshair', 
                position: 'relative',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1
              }}
            >
              {isLoadingImage && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)', 
                  zIndex: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">Loading map...</Typography>
                </Box>
              )}
              <Stage
                ref={stageRef}
                width={containerSize.width}
                height={containerSize.height}
                scaleX={zoom}
                scaleY={zoom}
                x={stagePosition.x}
                y={stagePosition.y}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onWheel={handleWheel}
                draggable={tool === 'select'}
                onDragEnd={handleDragEnd}
              >
                <Layer>
                  {/* Map Image */}
                  {mapImage && (
                    <KonvaImage
                      image={mapImage}
                      x={0}
                      y={0}
                      width={imageSize.width}
                      height={imageSize.height}
                    />
                  )}

                  {/* Walls - convert from world coords to pixel coords if needed */}
                  {walls.map((wall) => {
                    // Validate points
                    const pts = wall.points;
                    if (!pts || pts.length < 4 || pts.some((p: any) => typeof p !== 'number' || isNaN(p))) {
                      console.warn('[MapEditor] Invalid wall points:', wall.id, pts);
                      return null;
                    }
                    
                    // Detect if coordinates are in world space (meters) vs pixel space
                    // World coords are typically -20 to +20 range; pixel coords are 0 to imageSize
                    // Use pixel_points if available (pre-computed), otherwise check if conversion needed
                    let renderPts: number[];
                    const pixelPts = (wall as any).pixel_points;
                    if (pixelPts && pixelPts.length >= 4) {
                      renderPts = pixelPts;
                    } else {
                      const maxAbs = Math.max(Math.abs(pts[0]), Math.abs(pts[1]), Math.abs(pts[2]), Math.abs(pts[3]));
                      const isWorldCoords = maxAbs < 50 && imageSize.width > 50;
                      
                      if (isWorldCoords && mapData?.resolution) {
                        // Convert world coordinates to pixel coordinates
                        const p1 = worldToPixel(pts[0], pts[1]);
                        const p2 = worldToPixel(pts[2], pts[3]);
                        renderPts = [p1.x, p1.y, p2.x, p2.y];
                      } else {
                        renderPts = pts;
                      }
                    }
                    
                    if (walls.indexOf(wall) === 0) {
                      console.log('[MapEditor] Wall render:', wall.name, 'raw:', pts, 'render:', renderPts, 'imgSize:', imageSize);
                    }
                    
                    return (
                      <React.Fragment key={wall.id}>
                        <Line 
                          points={renderPts} 
                          stroke="#ff0000" 
                          strokeWidth={Math.max(3, 5 / zoom)} 
                          lineCap="round"
                          listening={false}
                        />
                        <Circle x={renderPts[0]} y={renderPts[1]} radius={Math.max(2, 4 / zoom)} fill="#ff0000" listening={false} />
                        <Circle x={renderPts[2]} y={renderPts[3]} radius={Math.max(2, 4 / zoom)} fill="#ff0000" listening={false} />
                        <Text 
                          x={(renderPts[0] + renderPts[2]) / 2} 
                          y={(renderPts[1] + renderPts[3]) / 2 - 16 / zoom} 
                          text={wall.name || `Wall ${walls.indexOf(wall) + 1}`} 
                          fill="#ff0000" 
                          fontSize={12 / zoom}
                          fontStyle="bold"
                          listening={false}
                        />
                      </React.Fragment>
                    );
                  })}

                  {/* Zones - convert from world coords to pixel coords if needed */}
                  {zones.map((zone) => {
                    const pts = zone.points;
                    if (!pts || pts.length < 4 || pts.some((p: any) => typeof p !== 'number' || isNaN(p))) {
                      console.warn('[MapEditor] Invalid zone points:', zone.id, pts);
                      return null;
                    }
                    
                    let renderX: number, renderY: number, renderW: number, renderH: number;
                    const maxAbs = Math.max(Math.abs(pts[0]), Math.abs(pts[1]), Math.abs(pts[2]), Math.abs(pts[3]));
                    const isWorldCoords = maxAbs < 50 && imageSize.width > 50;
                    
                    if (isWorldCoords && mapData?.resolution) {
                      // Zone in world coords: [x, y, width, height] in meters
                      const p1 = worldToPixel(pts[0], pts[1]);
                      const p2 = worldToPixel(pts[0] + pts[2], pts[1] + pts[3]);
                      renderX = Math.min(p1.x, p2.x);
                      renderY = Math.min(p1.y, p2.y);
                      renderW = Math.abs(p2.x - p1.x);
                      renderH = Math.abs(p2.y - p1.y);
                    } else {
                      // Pixel coords: [x, y, width, height]
                      renderX = pts[0];
                      renderY = pts[1];
                      renderW = pts[2];
                      renderH = pts[3];
                    }
                    
                    return (
                      <React.Fragment key={zone.id}>
                        <Rect 
                          x={renderX} 
                          y={renderY} 
                          width={renderW} 
                          height={renderH}
                          fill="rgba(255, 0, 0, 0.15)" 
                          stroke="#ff0000" 
                          strokeWidth={Math.max(2, 3 / zoom)} 
                          dash={[5 / zoom, 5 / zoom]}
                          listening={false}
                        />
                        <Text 
                          x={renderX + 5 / zoom} 
                          y={renderY + 5 / zoom} 
                          text={zone.name || 'Zone'} 
                          fill="#ff0000" 
                          fontSize={14 / zoom}
                          listening={false}
                        />
                      </React.Fragment>
                    );
                  })}

                  {/* Waypoints - rendered at pixel coordinates */}
                  {waypoints.map((wp) => {
                    // Use stored pixel coords or convert from world coords
                    const pixelX = (wp as any).pixel_x ?? worldToPixel(wp.x, wp.y).x;
                    const pixelY = (wp as any).pixel_y ?? worldToPixel(wp.x, wp.y).y;
                    
                    // Draw orientation arrow
                    const arrowLen = 20 / zoom;
                    const arrowX2 = pixelX + Math.cos(wp.theta) * arrowLen;
                    const arrowY2 = pixelY - Math.sin(wp.theta) * arrowLen; // Negative because canvas Y is flipped
                    
                    return (
                      <React.Fragment key={wp.id}>
                        <Circle x={pixelX} y={pixelY} radius={12 / zoom} fill="#00d4aa" stroke="#fff" strokeWidth={2 / zoom} />
                        {/* Orientation arrow */}
                        <Line 
                          points={[pixelX, pixelY, arrowX2, arrowY2]} 
                          stroke="#00d4aa" 
                          strokeWidth={3 / zoom} 
                          lineCap="round"
                        />
                        <Text x={pixelX + 15 / zoom} y={pixelY - 8 / zoom} text={wp.name} fill="#00d4aa" fontSize={12 / zoom} fontStyle="bold" />
                      </React.Fragment>
                    );
                  })}

                  {/* Current drawing */}
                  {isDrawing && tool === 'wall' && currentPoints.length >= 4 && (
                    <Line points={currentPoints} stroke="#ffab40" strokeWidth={4 / zoom} dash={[10 / zoom, 5 / zoom]} />
                  )}
                  {isDrawing && tool === 'zone' && currentPoints.length >= 4 && (
                    <Rect 
                      x={currentPoints[0]} 
                      y={currentPoints[1]} 
                      width={currentPoints[2]} 
                      height={currentPoints[3]}
                      fill="rgba(255, 171, 64, 0.2)" 
                      stroke="#ffab40" 
                      strokeWidth={2 / zoom} 
                      dash={[5 / zoom, 5 / zoom]} 
                    />
                  )}
                </Layer>
              </Stage>
            </Paper>
            
            {/* Instructions */}
            <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {tool === 'select' && '🖐 Drag to pan the map. Scroll to zoom. Select annotations to edit.'}
                {tool === 'wall' && '📏 Click and drag to draw a virtual wall (blocks robot navigation)'}
                {tool === 'zone' && '🚫 Click and drag to create a no-go zone (robot will avoid this area)'}
                {tool === 'waypoint' && '📍 Click to place a navigation waypoint (for patrol routes)'}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <Card sx={{ width: 280, flexShrink: 0 }}>
          <CardContent sx={{ height: '100%', overflow: 'auto' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Annotations</Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Walls ({walls.length})
            </Typography>
            <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
              {walls.map((wall, idx) => (
                <ListItem key={wall.id} sx={{ py: 0 }}>
                  <Box sx={{ width: 16, height: 2, bgcolor: '#ff0000', mr: 1, borderRadius: 1, flexShrink: 0 }} />
                  <ListItemText 
                    primary={wall.name || `Wall ${idx + 1}`} 
                    primaryTypographyProps={{ variant: 'body2' }} 
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => handleDeleteWall(wall.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {walls.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                  No walls drawn
                </Typography>
              )}
            </List>

            <Divider sx={{ my: 1 }} />

            <Typography variant="body2" color="text.secondary">
              No-Go Zones ({zones.length})
            </Typography>
            <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
              {zones.map((zone) => (
                <ListItem key={zone.id} sx={{ py: 0 }}>
                  <ListItemText 
                    primary={zone.name} 
                    secondary={zone.type} 
                    primaryTypographyProps={{ variant: 'body2' }} 
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => handleDeleteZone(zone.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {zones.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                  No zones defined
                </Typography>
              )}
            </List>

            <Divider sx={{ my: 1 }} />

            <Typography variant="body2" color="text.secondary">
              Waypoints ({waypoints.length})
            </Typography>
            <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
              {waypoints.map((wp) => (
                <ListItem key={wp.id} sx={{ py: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#00d4aa', mr: 1, flexShrink: 0 }} />
                  <ListItemText 
                    primary={wp.name} 
                    secondary={`(${wp.x.toFixed(2)}, ${wp.y.toFixed(2)}, ${Math.round(wp.theta * 180 / Math.PI)}°)`} 
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} 
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => handleDeleteWaypoint(wp.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {waypoints.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                  No waypoints set
                </Typography>
              )}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Waypoint Name Dialog */}
      <Dialog open={waypointDialog.open} onClose={() => setWaypointDialog({ open: false, x: 0, y: 0, yaw: 0 })} maxWidth="sm" fullWidth>
        <DialogTitle>Add Waypoint</DialogTitle>
        <DialogContent>
          <TextField 
            autoFocus 
            label="Waypoint Name" 
            value={waypointName} 
            onChange={(e) => setWaypointName(e.target.value)}
            fullWidth 
            sx={{ mt: 1 }} 
            placeholder="e.g., home, charging, station_1" 
            helperText="Use 'home' and 'charging' for required waypoints"
          />
          <TextField
            label="Orientation (Yaw)"
            type="number"
            value={waypointDialog.yaw}
            onChange={(e) => setWaypointDialog({ ...waypointDialog, yaw: parseFloat(e.target.value) || 0 })}
            fullWidth
            sx={{ mt: 2 }}
            InputProps={{
              endAdornment: <Typography variant="caption" color="text.secondary">degrees</Typography>
            }}
            helperText="Robot orientation when reaching this waypoint (0° = facing right, 90° = facing up)"
          />
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Pixel Position: ({waypointDialog.x.toFixed(0)}, {waypointDialog.y.toFixed(0)})
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
              World Position: ({pixelToWorld(waypointDialog.x, waypointDialog.y).x.toFixed(2)}m, {pixelToWorld(waypointDialog.x, waypointDialog.y).y.toFixed(2)}m)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaypointDialog({ open: false, x: 0, y: 0, yaw: 0 })}>Cancel</Button>
          <Button variant="contained" onClick={handleAddWaypoint} disabled={!waypointName.trim()}>Add Waypoint</Button>
        </DialogActions>
      </Dialog>

      {/* Robot Pose Waypoint Dialog */}
      <Dialog open={robotPoseDialog.open} onClose={() => setRobotPoseDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GpsFixed color="success" />
          Add Waypoint at Robot Position
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will add a waypoint at the robot's current position. Make sure the robot is properly localized.
          </Alert>
          <TextField 
            autoFocus 
            label="Waypoint Name" 
            value={waypointName} 
            onChange={(e) => setWaypointName(e.target.value)}
            fullWidth 
            sx={{ mt: 1 }} 
            placeholder="e.g., home, charging, station_1" 
            helperText="Use 'home' and 'charging' for required waypoints"
          />
          {robotPose && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Current Robot Position:
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                X: {robotPose.x.toFixed(4)} m
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                Y: {robotPose.y.toFixed(4)} m
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                θ: {((robotPose.theta || 0) * 180 / Math.PI).toFixed(1)}°
              </Typography>
            </Box>
          )}
          {!robotPose && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Robot pose not available. The robot must be online and localized.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRobotPoseDialog({ open: false }); setWaypointName(''); }}>Cancel</Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleAddWaypointFromRobotPose} 
            disabled={!waypointName.trim() || !robotPose}
            startIcon={<MyLocation />}
          >
            Add Waypoint
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={3000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        message={snackbar.message} 
      />
    </Box>
  );
};

export default MapEditor;
